import { GraphQLFormattedError } from 'graphql'
import { FC } from 'react'

import { CollectionLayoutFragmentFragment } from '../../../../__generated__/graphql/catalog/graphql'
import { ContentfulPageCollectionViewHeroSection } from '../../../../app/_components/pages/contentful/sections/ContentfulPageCollectionViewHeroSection.client'
import { ContentfulPageTabSection } from '../../../../app/_components/pages/contentful/sections/ContentfulPageTabSection.client'
import { ContentfulPageVideoSection } from '../../../../app/_components/pages/contentful/sections/ContentfulPageVideoSection.client'
import { NotFoundPage } from '../../../../app/_components/pages/NotFound.client'
import { TrackPageView } from '../../../../app/_providers/TrackingProvider.client'

import { CollectionPageSkeleton } from './CollectionPageSkeleton'
import { CollectionGridSection } from './sections/CollectionGridSection'
import { GenderNavSection } from './sections/GenderNavSection'
import { HeroFullBleedSection } from './sections/HeroFullBleedSection'
import { VideoModalBannerSection } from './sections/VideoModalBannerSection'
import { SeoContent } from './SeoContent'
import { CollectionPageTabSectionWrapper } from './styles'

import type { CollectionViewSection } from '../../../../types/graphql'

export interface CollectionViewProps {
  collectionView?: Pick<CollectionLayoutFragmentFragment, 'name' | 'sections' | 'seoContent'>
  errors: Array<GraphQLFormattedError>
  handle: string
  loading: boolean
}

export const CollectionView: FC<CollectionViewProps> = ({
  collectionView,
  errors,
  handle,
  loading,
}) => {
  const didQueryError = errors.length > 0

  // Note: the original collection name looks like this: "Name | FIGS"
  const collectionNameBeforePipeline = collectionView?.name?.split(' | ')?.[0]

  if (loading) {
    return <CollectionPageSkeleton />
  }

  if (!collectionView || didQueryError) {
    return <NotFoundPage />
  }

  return (
    <>
      <TrackPageView
        pageViewHandle={collectionNameBeforePipeline ?? collectionView.name}
        pageViewCategory='collection'
      />
      {collectionView && (
        <main>
          {collectionView.sections.map((props, index) => renderSection(props, index, handle))}
          {collectionView.seoContent && <SeoContent content={collectionView.seoContent} />}
        </main>
      )}
    </>
  )
}

const renderSection = (
  props: CollectionViewSection,
  index: number,
  collectionViewHandle: string
): JSX.Element | null => {
  const key = index
  switch (props.__typename) {
    case 'CollectionGridSection':
      return (
        <CollectionGridSection key={key} {...props} collectionViewHandle={collectionViewHandle} />
      )
    case 'CollectionViewHeroSection':
      return (
        <ContentfulPageCollectionViewHeroSection
          key={key}
          data={{
            ...props,
            ctaLink: null,
            ctaText: null,
          }}
          pageSectionIndex={index}
        />
      )
    case 'GenderNavSection':
      return <GenderNavSection key={key} {...props} />
    case 'HeroFullBleedSection':
      return <HeroFullBleedSection key={key} {...props} isCriticalImage={index === 0} />
    case 'VideoModalBannerSection':
      return <VideoModalBannerSection key={key} {...props} />
    case 'VideoSection':
      return <ContentfulPageVideoSection key={key} data={props} pageSectionIndex={index} />
    case 'PageTabSection':
      return (
        <CollectionPageTabSectionWrapper index={index} key={key}>
          <ContentfulPageTabSection data={props} pageSectionIndex={index} />
        </CollectionPageTabSectionWrapper>
      )
    default:
      return null
  }
}
